// =====================================================================
// Per-loan write sequence — stale-read guard
// =====================================================================
// Every loan mutation responds with the FULL fresh deal, and ~160 call
// sites dispatch that response straight into `loanDrilldown`. A loan GET
// that was already in flight when a mutation landed resolves AFTER it and
// replaces the freshly-written loan with the pre-write one — the user's
// save appears to revert a beat after it succeeded. (Seen on the Documents
// tab: a rename POST followed by an in-flight file fetch's loan refetch.)
//
// The fix is a monotonic "a write landed for this loan" counter. A read
// snapshots the counter when the request STARTS; if the counter moved by
// the time the response arrives, a newer write already wrote the loan into
// Redux and this read's payload is stale — drop it rather than apply it.
// The mutation response itself carries the fresh loan, so nothing is lost.
//
// Counting is per-loan (not global) so a write on one loan can't suppress a
// legitimate read of another. The bump is driven from the axios layer by
// pulling the deal id out of the request path — every loan-scoped write
// route ends in `/{dealId}` — which keeps this out of the individual hooks.

// loanId -> number of writes applied for that loan this session.
const seqByLoan = new Map();

// Mongo ObjectId as it appears in a route segment.
const OID_RE = /^[a-f\d]{24}$/i;

const key = (loanId) => String(loanId || "");

export const loanWriteSeq = (loanId) => seqByLoan.get(key(loanId)) || 0;

export const bumpLoanWriteSeq = (loanId) => {
  const k = key(loanId);
  if (!k) return 0;
  const next = (seqByLoan.get(k) || 0) + 1;
  seqByLoan.set(k, next);
  return next;
};

/**
 * Bump the counter for every ObjectId-shaped segment in a mutating request's
 * path. A path carries at most one deal id in practice; bumping each id-shaped
 * segment keeps this robust to routes that nest one (e.g. `.../{dealId}/{docId}`)
 * at the cost of an extra, harmless counter entry for the non-deal id.
 */
export const bumpLoanWriteSeqForPath = (path) => {
  if (!path) return;
  for (const segment of String(path).split(/[/?#]/)) {
    if (OID_RE.test(segment)) bumpLoanWriteSeq(segment);
  }
};

/**
 * True when a write for `loanId` landed after `token` was taken — i.e. the
 * read that took the token is now stale and must not be applied.
 */
export const isLoanReadStale = (loanId, token) => loanWriteSeq(loanId) !== token;
